import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { blogService } from '../../services/blogService';
import AdminLayout from '../../components/AdminLayout';
import RichTextEditor from '../../components/RichTextEditor';

const AdminBlogEditor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;

    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [blogForm, setBlogForm] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        featuredImage: '',
        category: 'Interior Design',
        tags: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: '',
        published: false
    });

    useEffect(() => {
        if (isEditing) {
            loadBlog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadBlog = async () => {
        try {
            setLoading(true);
            const blog = await blogService.getBlogById(id);
            setBlogForm({
                title: blog.title,
                slug: blog.slug,
                excerpt: blog.excerpt,
                content: blog.content,
                featuredImage: blog.featuredImage,
                category: blog.category,
                tags: blog.tags?.join(', ') || '',
                metaTitle: blog.metaTitle,
                metaDescription: blog.metaDescription,
                metaKeywords: blog.metaKeywords || '',
                published: blog.published
            });
        } catch (err) {
            console.error('Error loading blog:', err);
            alert('Error loading blog. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBlogFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBlogForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Auto-generate slug from title
        if (name === 'title' && !isEditing) {
            const slug = value.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '');
            setBlogForm(prev => ({ ...prev, slug }));
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image size must be less than 5MB');
            return;
        }

        try {
            setUploadingImage(true);
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/upload/image`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const data = await response.json();
            const imageUrl = `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${data.url}`;
            
            setBlogForm(prev => ({ ...prev, featuredImage: imageUrl }));
            alert('Image uploaded successfully!');
        } catch (err) {
            console.error('Error uploading image:', err);
            alert('Error uploading image. Please try again.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const blogData = {
                ...blogForm,
                tags: blogForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
            };

            if (isEditing) {
                await blogService.updateBlog(id, blogData);
                alert('Blog updated successfully!');
            } else {
                await blogService.createBlog(blogData);
                alert('Blog created successfully!');
            }

            navigate('/admin/blogs');
        } catch (err) {
            console.error('Error saving blog:', err);
            alert('Error saving blog. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEditing) {
        return (
            <AdminLayout>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '60vh',
                    fontSize: '18px',
                    color: 'rgba(245, 241, 237, 0.6)'
                }}>
                    Loading blog...
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div>
                {/* Header Section */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '40px'
                }}>
                    <div>
                        <h1 style={{
                            fontSize: '32px',
                            fontWeight: '300',
                            color: 'var(--text-light)',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <span style={{ fontSize: '36px' }}>{isEditing ? '✏️' : '📝'}</span>
                            {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
                        </h1>
                        <p style={{
                            fontSize: '14px',
                            color: 'rgba(245, 241, 237, 0.6)'
                        }}>
                            {isEditing ? 'Update your blog post details' : 'Fill in the details to create a new blog post'}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/admin/blogs')}
                        style={{
                            padding: '12px 24px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(201, 169, 110, 0.3)',
                            borderRadius: '10px',
                            color: 'var(--text-light)',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                            e.target.style.borderColor = 'var(--accent-color)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                            e.target.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                        }}
                    >
                        ← Back to Blogs
                    </button>
                </div>

                {/* Blog Form */}
                <div style={{
                    background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.08) 0%, rgba(201, 169, 110, 0.03) 100%)',
                    border: '1px solid rgba(201, 169, 110, 0.2)',
                    padding: '40px',
                    borderRadius: '16px'
                }}>
                    <form onSubmit={handleSubmit}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                            {/* Title */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={blogForm.title}
                                    onChange={handleBlogFormChange}
                                    required
                                    placeholder="Enter blog title"
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* Slug */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Slug * <span style={{ fontSize: '12px', color: 'rgba(245, 241, 237, 0.5)', fontWeight: '400' }}>(URL-friendly version of title)</span></label>
                                <input
                                    type="text"
                                    name="slug"
                                    value={blogForm.slug}
                                    onChange={handleBlogFormChange}
                                    required
                                    placeholder="blog-url-slug"
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'monospace'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* Excerpt */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Excerpt * <span style={{ fontSize: '12px', color: 'rgba(245, 241, 237, 0.5)', fontWeight: '400' }}>(Brief description for preview)</span></label>
                                <textarea
                                    name="excerpt"
                                    value={blogForm.excerpt}
                                    onChange={handleBlogFormChange}
                                    rows="3"
                                    required
                                    placeholder="Write a brief description..."
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* Content */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Content * <span style={{ fontSize: '12px', color: 'rgba(245, 241, 237, 0.5)', fontWeight: '400' }}>(Full blog content as rich editor)</span></label>
                                <RichTextEditor
                                    value={blogForm.content}
                                    onChange={(html) => setBlogForm(prev => ({ ...prev, content: html }))}
                                    placeholder="Write your blog content here... You can format text, add images, create lists, and more!"
                                />
                            </div>

                            {/* Featured Image */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Featured Image *</label>
                                
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            disabled={uploadingImage}
                                            style={{
                                                width: '100%',
                                                padding: '14px 18px',
                                                background: 'rgba(255, 255, 255, 0.05)',
                                                border: '1px solid rgba(201, 169, 110, 0.2)',
                                                borderRadius: '10px',
                                                color: 'var(--text-light)',
                                                fontSize: '15px',
                                                outline: 'none',
                                                transition: 'all 0.3s ease',
                                                cursor: uploadingImage ? 'not-allowed' : 'pointer'
                                            }}
                                        />
                                        {uploadingImage && (
                                            <p style={{
                                                fontSize: '13px',
                                                color: 'var(--accent-color)',
                                                marginTop: '8px'
                                            }}>⏳ Uploading image...</p>
                                        )}
                                        <p style={{
                                            fontSize: '12px',
                                            color: 'rgba(245, 241, 237, 0.5)',
                                            marginTop: '8px'
                                        }}>Max file size: 5MB. Supported formats: JPG, PNG, GIF, WebP</p>
                                    </div>
                                    
                                    {blogForm.featuredImage && (
                                        <div style={{
                                            width: '120px',
                                            height: '120px',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            border: '2px solid rgba(201, 169, 110, 0.3)',
                                            flexShrink: 0
                                        }}>
                                            <img
                                                src={blogForm.featuredImage}
                                                alt="Featured preview"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Category */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Category</label>
                                <input
                                    type="text"
                                    name="category"
                                    value={blogForm.category}
                                    onChange={handleBlogFormChange}
                                    placeholder="Interior Design"
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* Tags */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Tags (comma separated)</label>
                                <input
                                    type="text"
                                    name="tags"
                                    value={blogForm.tags}
                                    onChange={handleBlogFormChange}
                                    placeholder="design, interior, luxury"
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* SEO Section Header */}
                            <div style={{
                                gridColumn: '1 / -1',
                                marginTop: '20px',
                                paddingTop: '30px',
                                borderTop: '1px solid rgba(201, 169, 110, 0.2)'
                            }}>
                                <h3 style={{
                                    fontSize: '18px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)',
                                    marginBottom: '5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span style={{ fontSize: '20px' }}>🔍</span>
                                    SEO Settings
                                </h3>
                                <p style={{
                                    fontSize: '13px',
                                    color: 'rgba(245, 241, 237, 0.5)'
                                }}>
                                    Optimize your blog for search engines
                                </p>
                            </div>

                            {/* Meta Title */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Meta Title *</label>
                                <input
                                    type="text"
                                    name="metaTitle"
                                    value={blogForm.metaTitle}
                                    onChange={handleBlogFormChange}
                                    required
                                    placeholder="SEO title for search engines"
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* Meta Description */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Meta Description *</label>
                                <textarea
                                    name="metaDescription"
                                    value={blogForm.metaDescription}
                                    onChange={handleBlogFormChange}
                                    rows="3"
                                    required
                                    placeholder="SEO description for search engines"
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* Meta Keywords */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-light)'
                                }}>Meta Keywords</label>
                                <input
                                    type="text"
                                    name="metaKeywords"
                                    value={blogForm.metaKeywords}
                                    onChange={handleBlogFormChange}
                                    placeholder="keyword1, keyword2, keyword3"
                                    style={{
                                        width: '100%',
                                        padding: '14px 18px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(201, 169, 110, 0.2)',
                                        borderRadius: '10px',
                                        color: 'var(--text-light)',
                                        fontSize: '15px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'var(--accent-color)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                    }}
                                />
                            </div>

                            {/* Publish Checkbox */}
                            <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    fontSize: '15px',
                                    color: 'var(--text-light)',
                                    cursor: 'pointer',
                                    padding: '16px',
                                    background: 'rgba(40, 167, 69, 0.1)',
                                    border: '1px solid rgba(40, 167, 69, 0.3)',
                                    borderRadius: '10px',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(40, 167, 69, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(40, 167, 69, 0.1)';
                                }}>
                                    <input
                                        type="checkbox"
                                        name="published"
                                        checked={blogForm.published}
                                        onChange={handleBlogFormChange}
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            cursor: 'pointer',
                                            accentColor: '#28a745'
                                        }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '600', marginBottom: '2px' }}>Publish immediately</div>
                                        <div style={{ fontSize: '12px', color: 'rgba(245, 241, 237, 0.6)' }}>
                                            Make this blog post visible to the public
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: '16px',
                            marginTop: '40px',
                            paddingTop: '30px',
                            borderTop: '1px solid rgba(201, 169, 110, 0.2)'
                        }}>
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '16px 32px',
                                    background: loading ? 'rgba(201, 169, 110, 0.3)' : 'linear-gradient(135deg, var(--accent-color) 0%, #B8935D 100%)',
                                    border: 'none',
                                    borderRadius: '10px',
                                    color: '#000',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 8px 25px rgba(201, 169, 110, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                {loading ? (
                                    <>⏳ Saving...</>
                                ) : (
                                    <>{isEditing ? '✓ Update Blog' : '✓ Create Blog'}</>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/admin/blogs')}
                                disabled={loading}
                                style={{
                                    padding: '16px 32px',
                                    background: 'transparent',
                                    border: '1px solid rgba(201, 169, 110, 0.3)',
                                    borderRadius: '10px',
                                    color: 'var(--text-light)',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                        e.target.style.borderColor = 'var(--accent-color)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'transparent';
                                    e.target.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminBlogEditor;

